<template>
  <v-container id="org-licenses">
    <v-alert v-if="!isSuperuser" type="error" outlined>
      Kein Zugriff. Diese Ansicht ist nur für Administratoren verfügbar.
    </v-alert>
    <v-alert v-else-if="organization && organization.level !== LEVEL_OPTIONS.REGION" type="info" outlined>
      Die Lizenzverwaltung ist nur für Organisationen auf Regionsebene verfügbar.
    </v-alert>
    <template v-else-if="organization">
      <h2>Lizenzverwaltung</h2>
      <h3>{{ organization.name }}</h3>

      <v-alert
        type="warning"
        dense
        outlined
        class="mt-4"
        :icon="mdiFlaskOutline"
      >
        Konzept-Vorschau: Lizenzen sind aktuell noch nicht im Backend abgebildet. Die Zuordnung
        auf dieser Seite wird nur lokal in diesem Browser gespeichert und hat keine Auswirkung
        auf echte Verträge, Geräte oder Features.
      </v-alert>

      <v-card outlined class="mt-4">
        <v-tabs v-model="selectedTabIndex" show-arrows>
          <v-tab v-for="option in productOptions" :key="option.kind + ':' + option.key">
            {{ option.text }}
          </v-tab>
        </v-tabs>
        <v-card-text v-if="isHardwareTab">
          <v-row align="center">
            <v-col v-for="row in hardwareDeviceTypeCounts" :key="row.type" cols="6" sm="3" md="2">
              <div class="text-caption text--secondary">{{ row.label }}</div>
              <div class="text-h6">{{ row.count }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption text--secondary">Abrechnungsplan ({{ organization.name }})</div>
              <div v-if="!regionBillingPlans.length" class="text-h6">-</div>
              <div v-for="plan in regionBillingPlans" :key="plan" class="text-h6">{{ plan }}</div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text v-else>
          <v-row align="center">
            <v-col cols="6" sm="2" md="2">
              <div class="text-caption text--secondary">Verfügbar</div>
              <div class="text-h6" :class="{ 'error--text': isOverAssigned }">{{ remainingLicenses }}</div>
            </v-col>
            <v-col cols="6" sm="2" md="2">
              <div class="text-caption text--secondary">Vergeben</div>
              <div class="text-h6">{{ assignedLicenseCount }}</div>
            </v-col>
            <v-col cols="12" sm="2" md="2">
              <v-text-field
                v-model.number="purchasedLicenses"
                type="number"
                min="0"
                label="Gekaufte Lizenzen"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <div class="text-caption text--secondary">Abrechnungsplan ({{ organization.name }})</div>
              <div v-if="!regionBillingPlans.length" class="text-h6">-</div>
              <div v-for="plan in regionBillingPlans" :key="plan" class="text-h6">{{ plan }}</div>
            </v-col>
          </v-row>
          <v-alert
            v-if="isOverAssigned"
            type="error"
            dense
            text
            class="mt-4 mb-0"
          >
            Es sind mehr Lizenzen für {{ selectedProductLabel }} vergeben ({{ assignedLicenseCount }}) als eingekauft wurden ({{ purchasedLicenses }}).
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card outlined class="mt-6">
        <v-data-table
          :headers="headers"
          :items="sortedChildRows"
          item-key="url"
          :loading="loading > 0"
          disable-sort
          disable-pagination
          hide-default-footer
        >
          <template #item.license="{ item }">
            <v-switch
              v-model="getAssignment(item.url).licensed"
              color="primary"
              hide-details
              class="mt-0 pt-0"
              @change="licensed => onLicenseToggle(item.url, licensed)"
            />
          </template>
          <template #item.validFrom="{ item }">
            <v-text-field
              v-if="getAssignment(item.url).licensed"
              v-model="getAssignment(item.url).validFrom"
              type="date"
              dense
              hide-details
              style="max-width: 160px"
              @change="onLicenseAssignmentChange"
            />
            <span v-else>-</span>
          </template>
          <template #item.validUntil="{ item }">
            <v-text-field
              v-if="!getAssignment(item.url).licensed && getAssignment(item.url).validUntil"
              v-model="getAssignment(item.url).validUntil"
              type="date"
              dense
              hide-details
              style="max-width: 160px"
              @change="onLicenseAssignmentChange"
            />
            <span v-else>-</span>
          </template>
          <template #item.name="{ item }">
            <div class="d-flex align-center" :style="{ paddingLeft: (item.depth * 28) + 'px' }">
              <v-icon v-if="item.depth > 0" size="16" color="grey" class="mr-1">{{ mdiSubdirectoryArrowRight }}</v-icon>
              <organization-icon :organization="item" size="22" class="mr-1" />
              {{ item.shortName }}
            </div>
          </template>
          <template #item.identifier="{ item }">
            {{ item.identifier || '-' }}
          </template>
          <template #item.contracts="{ item }">
            <span v-if="!item.contracts.length">-</span>
            <div v-for="contract in item.contracts" :key="contract.id">
              {{ contract.label }}
            </div>
          </template>
          <template #item.hardwareSummary="{ item }">
            {{ item.hardwareSummary || '-' }}
          </template>
        </v-data-table>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { mdiFlaskOutline, mdiSubdirectoryArrowRight } from '@mdi/js'
import { LEVEL_OPTIONS } from '@/lib/level-options'
import { getLatestVersion, isCurrentlyValid } from '@/lib/sort-utils'
import { deviceTypeToClassName, DEVICE_TYPES } from '@/lib/device-db'
import { shortenParish } from '@/lib/regex-tools'
import { FEATURE_RESOURCE_TYPE_MAP } from '@/lib/settings-tools'
import OrganizationIcon from '@/components/icons/OrganizationIcon.vue'
import {
  FETCH_ORGANIZATIONS,
  FETCH_CONTRACTS,
  FETCH_BILLING_PLANS,
  FETCH_BILLING_PLAN_CONTRACT_ASSOCIATION,
  FETCH_DEVICES,
  FETCH_LOCATIONS
} from '@/store/action-types'

const DEFAULT_VALID_FROM = '2026-01-01'

const CONTRACT_TYPE_LABELS = {
  op: 'E-Commerce',
  ps: 'KNB'
}

const HARDWARE_PRODUCT_KEY = 'hardware'
const isHardwareDeviceType = deviceType => DEVICE_TYPES[deviceType]?.hardware === true

// Fixed order for the common products; anything else found in use is appended
// afterwards, sorted alphabetically.
const PINNED_PRODUCT_KEYS = [
  'bart', // spende.app
  'john', // kollekte.app
  HARDWARE_PRODUCT_KEY,
  'cash',
  FEATURE_RESOURCE_TYPE_MAP.VERANSTALTUNG_APP_FEATURE,
  FEATURE_RESOURCE_TYPE_MAP.KIRCHGELD_DIGITAL_FEATURE
]

export default {
  name: 'org-licenses',
  components: { OrganizationIcon },
  data () {
    return {
      LEVEL_OPTIONS,
      mdiFlaskOutline,
      mdiSubdirectoryArrowRight,
      selectedTabIndex: 0,
      licenseState: {},
      // Snapshot of which orgs were licensed at load time, per product - used only
      // to decide sort order, so toggling a switch doesn't yank the row to a new
      // position while you're working. Captured once in initializeLicenseState.
      initialLicensedByProduct: {},
      billingPlanContractAssociations: {}
    }
  },
  computed: {
    ...mapState(['loading']),
    ...mapGetters('organization', {
      organization: 'currentOrganization'
    }),
    ...mapGetters('organization', ['allOrganizations', 'descendants', 'organizationRelevantDeviceTypesForUrl', 'effectiveOrganizationFeaturesByUrl']),
    ...mapGetters('user', ['isSuperuser']),
    ...mapGetters('contracts', ['contractsByOrganization']),
    ...mapGetters('billing', ['billingPlanByUrl']),
    ...mapGetters('location', ['locationsLookup']),
    ...mapState('device', ['devicesByOrganization']),
    storageKey () {
      // Versioned so a future change to the assignment shape starts fresh instead
      // of silently merging incompatible old data (e.g. a raw boolean where an
      // { licensed, validFrom } object is now expected).
      return this.organization ? `licenseMockup:v2:${this.organization.url}` : null
    },
    children () {
      if (!this.organization) {
        return []
      }
      return (this.descendants[this.organization.url] ?? [])
        .map(url => this.allOrganizations[url])
        .filter(Boolean)
    },
    // Every product a descendant is currently entitled to, across all of them -
    // this drives the tabs, independent of which children already have which one.
    // Covers both devices (spende.app/kollekte.app/hardware, using the *effective*
    // device types - own + inherited from a parent, matching what's shown
    // everywhere else in the app) AND org-level features (veranstaltung.app,
    // kirchgeld.digital, notification module, etc.) - not just devices, since a
    // license here can back either kind of product. All non-hardware device types
    // get their own tab; every hardware device (Klingelbeutel, Bildschirmsäule, ...)
    // is summarized into one "Hardware" tab instead of one per device type.
    productOptions () {
      const usedDeviceTypes = new Set()
      this.children.forEach(child => {
        this.organizationRelevantDeviceTypesForUrl(child.url).forEach(deviceType => usedDeviceTypes.add(deviceType))
      })
      const hasHardware = Array.from(usedDeviceTypes).some(isHardwareDeviceType)

      const deviceProducts = Array.from(usedDeviceTypes)
        .filter(deviceType => !isHardwareDeviceType(deviceType))
        .map(deviceType => ({ kind: 'device', key: deviceType, text: deviceTypeToClassName(deviceType) }))
      if (hasHardware) {
        deviceProducts.push({ kind: 'hardware', key: HARDWARE_PRODUCT_KEY, text: 'Hardware' })
      }

      const featureProducts = Object.values(FEATURE_RESOURCE_TYPE_MAP)
        .filter(resourcetype => this.children.some(child => this.effectiveOrganizationFeaturesByUrl[child.url]?.has(resourcetype)))
        .map(resourcetype => ({
          kind: 'feature',
          key: resourcetype,
          text: this.$t('organization.features.' + resourcetype)
        }))

      const allProducts = [...deviceProducts, ...featureProducts]
      const pinned = PINNED_PRODUCT_KEYS
        .map(key => allProducts.find(product => product.key === key))
        .filter(Boolean)
      const rest = allProducts
        .filter(product => !PINNED_PRODUCT_KEYS.includes(product.key))
        .sort((a, b) => a.text.localeCompare(b.text))

      return [...pinned, ...rest]
    },
    selectedProduct () {
      return this.productOptions[this.selectedTabIndex] ?? null
    },
    selectedProductKey () {
      return this.selectedProduct?.key ?? null
    },
    selectedProductLabel () {
      return this.selectedProduct?.text ?? ''
    },
    // Hardware isn't a licensable product like the others - it's just informational,
    // no toggles/validity/pool to manage, so the table and stats area both switch
    // to a read-only device-count view instead.
    isHardwareTab () {
      return this.selectedProduct?.kind === 'hardware'
    },
    headers () {
      if (this.isHardwareTab) {
        return [
          { text: 'Geräte', value: 'hardwareSummary', sortable: false },
          { text: 'Organisation', value: 'name', sortable: false },
          { text: 'Kennung', value: 'identifier', sortable: false },
          { text: 'Verträge', value: 'contracts', sortable: false }
        ]
      }
      return [
        { text: 'Lizenz', value: 'license', sortable: false },
        { text: 'Gültig ab', value: 'validFrom', sortable: false },
        { text: 'Gültig bis', value: 'validUntil', sortable: false },
        { text: 'Organisation', value: 'name', sortable: false },
        { text: 'Kennung', value: 'identifier', sortable: false },
        { text: 'Verträge', value: 'contracts', sortable: false }
      ]
    },
    // Real device instances (not just "has this type" booleans), keyed by device
    // type per descendant - so counts like "3x Klingelbeutel" are actual
    // quantities. GET {orgUrl}devices/?expand=contracts returns devices for the
    // WHOLE subtree under orgUrl, not just that org's own devices (that's what
    // OrganizationDevices.vue's "Unterorganisationen einschließen" toggle exists
    // to filter back down) - so fetching per descendant would double-count a
    // child's devices under every ancestor too. Instead this fetches once at the
    // region level and resolves each device's real owner (contract, falling back
    // to its current location), the same way OrganizationDevices.vue does it.
    hardwareDeviceCountsByOrg () {
      const devicesForRegion = this.devicesByOrganization[this.organization.url] ?? []
      const result = {}
      devicesForRegion.forEach(device => {
        if (!isHardwareDeviceType(device.type) || !device.active) {
          return
        }
        const ownerUrl = this.getDeviceOwnerUrl(device)
        if (!ownerUrl) {
          return
        }
        if (!result[ownerUrl]) {
          result[ownerUrl] = {}
        }
        result[ownerUrl][device.type] = (result[ownerUrl][device.type] ?? 0) + 1
      })
      return result
    },
    hardwareDeviceTypeCounts () {
      const totals = {}
      Object.values(this.hardwareDeviceCountsByOrg).forEach(orgCounts => {
        Object.entries(orgCounts).forEach(([type, count]) => {
          totals[type] = (totals[type] ?? 0) + count
        })
      })
      return Object.entries(totals)
        .map(([type, count]) => ({ type, label: deviceTypeToClassName(type), count }))
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    // Always every descendant, so a license can be newly assigned to a child that
    // doesn't have the product yet - not just the ones that already do.
    childRows () {
      // Hardware has no per-org license concept (see isHardwareTab), so it's
      // excluded here too - nothing needs its usage tracked per org.
      const products = this.productOptions.filter(product => product.kind !== 'hardware')
      return this.children.map(child => {
        const validContracts = (this.contractsByOrganization[child.url]?.validContracts ?? [])
          .filter(contract => ['op', 'ps'].includes(contract.type))

        const effectiveDeviceTypes = this.organizationRelevantDeviceTypesForUrl(child.url)
        const productUsage = {}
        products.forEach(product => {
          productUsage[product.key] = product.kind === 'device'
            ? effectiveDeviceTypes.has(product.key)
            : this.effectiveOrganizationFeaturesByUrl[child.url]?.has(product.key) ?? false
        })

        return {
          url: child.url,
          name: child.name,
          shortName: shortenParish(child.name),
          // "Kennung" = the org settings identifiers (e.g. KRO) - a free-form,
          // not-always-set dict that can hold several entries, distinct from
          // short_name (used for Lastschriften). Show every key that's set.
          identifier: Object.entries(child.identifiers ?? {})
            .filter(([, value]) => !!value)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', '),
          level: child.level,
          parentUrl: child.parent,
          productUsage,
          contracts: validContracts.map(contract => ({
            id: contract.id,
            label: `${CONTRACT_TYPE_LABELS[contract.type] ?? contract.type} (#${contract.id})`
          })),
          hardwareSummary: Object.entries(this.hardwareDeviceCountsByOrg[child.url] ?? {})
            .map(([type, count]) => `${count}x ${deviceTypeToClassName(type)}`)
            .join(', ')
        }
      })
    },
    // Walks the actual org hierarchy: each org's own children are placed directly
    // after it (not merely grouped with unrelated same-named-parent siblings),
    // licensed ones first within each sibling group, alphabetically as the
    // final tiebreaker. Uses the frozen initial-load snapshot for the "licensed"
    // tiebreaker (not the live assignments) so a row doesn't jump to a new
    // position the moment you toggle it - it keeps its place for the rest of
    // the session.
    sortedChildRows () {
      const initialLicensed = this.initialLicensedByProduct[this.selectedProductKey] ?? {}
      const rowsByParentUrl = {}
      this.childRows.forEach(row => {
        if (!rowsByParentUrl[row.parentUrl]) {
          rowsByParentUrl[row.parentUrl] = []
        }
        rowsByParentUrl[row.parentUrl].push(row)
      })

      const sortSiblings = rows => [...rows].sort((a, b) => {
        const aLicensed = initialLicensed[a.url] ?? false
        const bLicensed = initialLicensed[b.url] ?? false
        if (aLicensed !== bLicensed) {
          return aLicensed ? -1 : 1
        }
        return a.shortName.localeCompare(b.shortName)
      })

      const result = []
      const appendChildrenOf = (parentUrl, depth) => {
        sortSiblings(rowsByParentUrl[parentUrl] ?? []).forEach(row => {
          row.depth = depth
          result.push(row)
          appendChildrenOf(row.url, depth + 1)
        })
      }
      appendChildrenOf(this.organization.url, 0)
      return result
    },
    // Billing plans come in two scopes (KNB vs. E-Commerce) matching the contract
    // type. Hardware (Klingelbeutel, Bildschirmsäule, ...) is billed under KNB
    // ('ps' contracts); every other product (spende.app, kollekte.app, features,
    // ...) is billed under E-Commerce ('op' contracts) - so which one is shown
    // here follows the currently selected tab.
    regionBillingPlans () {
      const wantedContractType = this.selectedProduct?.kind === 'hardware' ? 'ps' : 'op'
      const validContracts = (this.contractsByOrganization[this.organization.url]?.validContracts ?? [])
        .filter(contract => contract.type === wantedContractType)

      const billingPlans = new Set()
      validContracts.forEach(contract => {
        const associations = this.billingPlanContractAssociations[contract.id] ?? []
        // A contract can have more than one currently-valid billing plan
        // association at once - show all of them, not just one "latest" pick.
        associations.filter(isCurrentlyValid).forEach(association => {
          const plan = this.billingPlanByUrl[association.billing_plan]
          if (plan) {
            billingPlans.add(plan.name)
          }
        })
      })
      return Array.from(billingPlans)
    },
    currentAssignments () {
      return this.licenseState[this.selectedProductKey]?.assignments ?? {}
    },
    purchasedLicenses: {
      get () {
        return this.licenseState[this.selectedProductKey]?.purchased ?? 0
      },
      set (value) {
        if (!this.licenseState[this.selectedProductKey]) {
          this.$set(this.licenseState, this.selectedProductKey, { purchased: 0, assignments: {} })
        }
        this.licenseState[this.selectedProductKey].purchased = value
        this.persistState()
      }
    },
    assignedLicenseCount () {
      return Object.values(this.currentAssignments).filter(assignment => assignment?.licensed).length
    },
    remainingLicenses () {
      return (this.purchasedLicenses || 0) - this.assignedLicenseCount
    },
    isOverAssigned () {
      return this.remainingLicenses < 0
    }
  },
  methods: {
    // Mirrors OrganizationDevices.vue's getOwnerOrganization: prefer the device's
    // currently valid contract, falling back to its physical location.
    getDeviceOwnerUrl (device) {
      const latestContractLink = getLatestVersion(device?.contracts ?? [])
      if (latestContractLink) {
        return latestContractLink.contract?.organization ?? null
      }
      if (!device?.current_location) {
        return null
      }
      return this.locationsLookup[device.current_location]?.organization ?? null
    },
    getAssignment (url) {
      if (!this.currentAssignments[url]) {
        this.$set(this.currentAssignments, url, { licensed: false, validFrom: DEFAULT_VALID_FROM, validUntil: null })
      }
      return this.currentAssignments[url]
    },
    onLicenseToggle (url, licensed) {
      const assignment = this.getAssignment(url)
      // Revoking a license marks when it stopped being valid; re-granting clears
      // that end date again since it's active once more.
      assignment.validUntil = licensed ? null : new Date().toISOString().substring(0, 10)
      this.persistState()
    },
    loadStoredState () {
      if (!this.storageKey) {
        return null
      }
      try {
        const raw = window.localStorage.getItem(this.storageKey)
        return raw ? JSON.parse(raw) : null
      } catch (e) {
        return null
      }
    },
    persistState () {
      if (!this.storageKey) {
        return
      }
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(this.licenseState))
      } catch (e) {
        // ignore quota / privacy-mode errors, this is a local-only prototype concept
      }
    },
    onLicenseAssignmentChange () {
      this.persistState()
    },
    initializeLicenseState () {
      const stored = this.loadStoredState() ?? {}
      const nextState = {}
      const nextInitialLicensed = {}

      this.productOptions.filter(option => option.kind !== 'hardware').forEach(option => {
        const productKey = option.key
        const defaultAssignments = {}
        let usageCount = 0
        this.childRows.forEach(row => {
          const alreadyHasIt = row.productUsage[productKey]
          defaultAssignments[row.url] = { licensed: alreadyHasIt, validFrom: DEFAULT_VALID_FROM, validUntil: null }
          if (alreadyHasIt) {
            usageCount++
          }
        })

        const storedProductState = stored[productKey]
        const assignments = { ...defaultAssignments, ...(storedProductState?.assignments ?? {}) }
        nextState[productKey] = {
          purchased: storedProductState?.purchased ?? (usageCount + 1),
          // Merge stored assignments over the computed defaults so descendants that
          // appeared after the last visit (not yet in localStorage) still get a value.
          assignments
        }

        const licensedByUrl = {}
        Object.keys(assignments).forEach(url => { licensedByUrl[url] = assignments[url].licensed })
        nextInitialLicensed[productKey] = licensedByUrl
      })

      this.licenseState = nextState
      this.initialLicensedByProduct = nextInitialLicensed
      this.persistState()
    },
    async loadBillingPlanAssociations () {
      const contracts = (this.contractsByOrganization[this.organization.url]?.validContracts ?? [])
        .filter(contract => ['op', 'ps'].includes(contract.type))

      const responses = await Promise.all(contracts.map(async contract => {
        const associations = await this.$store.dispatch('contracts/' + FETCH_BILLING_PLAN_CONTRACT_ASSOCIATION, {
          contractId: contract.id
        })
        return { contractId: contract.id, associations }
      }))

      responses.forEach(({ contractId, associations }) => {
        if (associations?.length) {
          this.$set(this.billingPlanContractAssociations, contractId, associations)
        }
      })
    }
  },
  watch: {
    organization: {
      immediate: false,
      handler (newVal, oldVal) {
        if (newVal && (!oldVal || newVal.id !== oldVal.id)) {
          this.selectedTabIndex = 0
          this.initializeLicenseState()
        }
      }
    }
  },
  async mounted () {
    if (!this.isSuperuser) {
      return
    }
    await Promise.all([
      this.$store.dispatch('organization/' + FETCH_ORGANIZATIONS),
      this.$store.dispatch('contracts/' + FETCH_CONTRACTS),
      this.$store.dispatch('billing/' + FETCH_BILLING_PLANS)
    ])
    await Promise.all([
      // Fetched once at the region level - it already covers the whole subtree.
      this.$store.dispatch('device/' + FETCH_DEVICES, this.organization.url),
      this.$store.dispatch('location/' + FETCH_LOCATIONS)
    ])
    await this.loadBillingPlanAssociations()
    this.initializeLicenseState()
  }
}
</script>
